import React, { useState } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SpeakerWaveIcon,
  EnvelopeIcon,
  BellIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import type { Tenant } from './TenantTable';

interface CommunicationMessage {
  type: 'email' | 'in_app' | 'system_announcement' | 'maintenance' | 'billing' | 'support';
  subject: string;
  content: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  recipients: 'admin_only' | 'all_users' | 'custom';
  customRecipients?: string[];
  scheduleFor?: Date;
  attachments?: File[];
}

interface MessageTemplate {
  id: string;
  name: string;
  type: CommunicationMessage['type'];
  subject: string;
  content: string;
  priority: CommunicationMessage['priority'];
}

interface TenantCommunicationProps {
  isOpen: boolean;
  tenants: Tenant[];
  selectedTenants: string[];
  onClose: () => void;
  onSendMessage: (message: CommunicationMessage, tenantIds: string[]) => void;
}

const TenantCommunication: React.FC<TenantCommunicationProps> = ({
  isOpen,
  tenants,
  selectedTenants,
  onClose,
  onSendMessage
}) => {
  const { branding } = useWhitelabel();
  const [message, setMessage] = useState<CommunicationMessage>({
    type: 'email',
    subject: '',
    content: '',
    priority: 'normal',
    recipients: 'admin_only'
  });
  const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'history'>('compose');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const messageTypes = [
    {
      id: 'email' as const,
      label: 'Email',
      description: 'Send email to tenant contacts',
      icon: EnvelopeIcon,
      color: 'text-blue-600 bg-blue-50 border-blue-200'
    },
    {
      id: 'in_app' as const,
      label: 'In-App Notification',
      description: 'Show notification in tenant dashboard',
      icon: BellIcon,
      color: 'text-purple-600 bg-purple-50 border-purple-200'
    },
    {
      id: 'system_announcement' as const,
      label: 'System Announcement',
      description: 'Important system-wide announcement',
      icon: SpeakerWaveIcon,
      color: 'text-green-600 bg-green-50 border-green-200'
    },
    {
      id: 'maintenance' as const,
      label: 'Maintenance Notice',
      description: 'Scheduled maintenance notification',
      icon: ExclamationTriangleIcon,
      color: 'text-orange-600 bg-orange-50 border-orange-200'
    },
    {
      id: 'billing' as const,
      label: 'Billing Update',
      description: 'Payment or billing related message',
      icon: ExclamationTriangleIcon,
      color: 'text-red-600 bg-red-50 border-red-200'
    },
    {
      id: 'support' as const,
      label: 'Support Message',
      description: 'Customer support communication',
      icon: InformationCircleIcon,
      color: 'text-gray-600 bg-gray-50 border-gray-200'
    }
  ];

  const messageTemplates: MessageTemplate[] = [
    {
      id: 'welcome',
      name: 'Welcome Message',
      type: 'email',
      subject: 'Welcome to {{platform_name}}!',
      content: `Dear {{tenant_name}},

Welcome to {{platform_name}}! We're excited to have you as part of our community.

Your account has been successfully set up and is ready to use. Here's what you can do next:

1. Log in to your dashboard at {{tenant_url}}
2. Complete your profile setup
3. Explore our features and documentation
4. Contact support if you have any questions

Best regards,
The {{platform_name}} Team`,
      priority: 'normal'
    },
    {
      id: 'maintenance',
      name: 'Maintenance Notification',
      type: 'maintenance',
      subject: 'Scheduled Maintenance: {{maintenance_date}}',
      content: `Dear {{tenant_name}},

We will be performing scheduled maintenance on {{maintenance_date}} from {{start_time}} to {{end_time}} ({{timezone}}).

During this time, you may experience:
- Brief service interruptions
- Temporary unavailability of certain features
- Slower response times

We apologize for any inconvenience and appreciate your patience as we work to improve our service.

If you have any questions, please contact our support team.

Best regards,
The {{platform_name}} Team`,
      priority: 'high'
    },
    {
      id: 'billing_reminder',
      name: 'Payment Reminder',
      type: 'billing',
      subject: 'Payment Reminder - Invoice {{invoice_number}}',
      content: `Dear {{tenant_name}},

This is a friendly reminder that your payment for invoice {{invoice_number}} is due on {{due_date}}.

Amount Due: {{amount}}
Invoice Date: {{invoice_date}}

To avoid any service interruption, please ensure your payment is processed before the due date.

You can view and pay your invoice by logging into your account at {{tenant_url}}.

If you have any questions about your billing, please contact our billing support team.

Best regards,
The {{platform_name}} Billing Team`,
      priority: 'high'
    },
    {
      id: 'feature_announcement',
      name: 'Feature Announcement',
      type: 'system_announcement',
      subject: 'New Feature: {{feature_name}}',
      content: `Dear {{tenant_name}},

We're excited to announce a new feature: {{feature_name}}!

{{feature_description}}

Key Benefits:
- {{benefit_1}}
- {{benefit_2}}
- {{benefit_3}}

To start using this feature, log into your dashboard and look for the "{{feature_name}}" section.

We'd love to hear your feedback! Please don't hesitate to reach out to our support team.

Best regards,
The {{platform_name}} Team`,
      priority: 'normal'
    }
  ];

  const updateMessage = (updates: Partial<CommunicationMessage>) => {
    setMessage(prev => ({ ...prev, ...updates }));
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      updateMessage({
        type: template.type,
        subject: template.subject,
        content: template.content,
        priority: template.priority
      });
    }
  };

  const interpolateTemplate = (text: string, tenant: Tenant): string => {
    const variables: Record<string, string> = {
      '{{platform_name}}': branding.companyName,
      '{{tenant_name}}': tenant.name ?? '',
      '{{tenant_url}}': `https://${tenant.domain ?? ''}`,
      '{{tenant_domain}}': tenant.domain ?? '',
      '{{maintenance_date}}': 'September 15, 2024',
      '{{start_time}}': '2:00 AM',
      '{{end_time}}': '4:00 AM',
      '{{timezone}}': 'UTC',
      '{{invoice_number}}': 'INV-001234',
      '{{due_date}}': 'September 30, 2024',
      '{{amount}}': '$99.00',
      '{{invoice_date}}': 'September 1, 2024',
      '{{feature_name}}': 'Advanced Analytics',
      '{{feature_description}}': 'Get deeper insights into your data with our new advanced analytics dashboard.',
      '{{benefit_1}}': 'Real-time data visualization',
      '{{benefit_2}}': 'Custom reporting capabilities',
      '{{benefit_3}}': 'Enhanced performance metrics'
    };

    let result = text;
    Object.entries(variables).forEach(([placeholder, value]) => {
      result = result.replace(new RegExp(placeholder, 'g'), value);
    });
    return result;
  };

  const validateMessage = (): boolean => {
    return !!(message.subject.trim() && message.content.trim());
  };

  const handleSendMessage = async () => {
    if (!validateMessage() || selectedTenants.length === 0) return;

    setIsSending(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await onSendMessage(message, selectedTenants);
      
      toast.success(`Message sent to ${selectedTenants.length} tenant${selectedTenants.length !== 1 ? 's' : ''}`);
      onClose();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
      setShowConfirmation(false);
    }
  };

  const getPriorityColor = (priority: CommunicationMessage['priority']): string => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'normal':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low':
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const selectedTenantData = tenants.filter(t => selectedTenants.includes(t.id));
  const selectedType = messageTypes.find(type => type.id === message.type);

  if (!isOpen) return null;

  const renderComposeTab = () => (
    <div className="space-y-6">
      {/* Recipients */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Recipients ({selectedTenants.length} tenant{selectedTenants.length !== 1 ? 's' : ''})
        </h4>
        <div className="bg-gray-50 rounded-lg p-3 max-h-24 overflow-y-auto">
          <div className="flex flex-wrap gap-2">
            {selectedTenantData.slice(0, 10).map((tenant) => (
              <span
                key={tenant.id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {tenant.name}
              </span>
            ))}
            {selectedTenantData.length > 10 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                +{selectedTenantData.length - 10} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Message Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Message Type
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {messageTypes.map((type) => (
            <div
              key={type.id}
              onClick={() => updateMessage({ type: type.id })}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                message.type === type.id ? type.color : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <type.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{type.label}</span>
              </div>
              <p className="text-xs text-gray-600">{type.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <select
            value={message.priority}
            onChange={(e) => updateMessage({ priority: e.target.value as CommunicationMessage['priority'] })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipient Type
          </label>
          <select
            value={message.recipients}
            onChange={(e) => updateMessage({ recipients: e.target.value as CommunicationMessage['recipients'] })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          >
            <option value="admin_only">Admin Only</option>
            <option value="all_users">All Users</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Subject *
        </label>
        <input
          type="text"
          value={message.subject}
          onChange={(e) => updateMessage({ subject: e.target.value })}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          placeholder="Enter message subject..."
        />
      </div>

      {/* Content */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Message Content *
          </label>
          <button
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            {isPreviewMode ? 'Edit' : 'Preview'}
          </button>
        </div>
        {isPreviewMode ? (
          <div className="bg-gray-50 border border-gray-300 rounded-md p-4 min-h-32">
            <div className="whitespace-pre-wrap text-sm text-gray-900">
              {selectedTenantData.length > 0
                ? interpolateTemplate(message.content, selectedTenantData[0])
                : message.content
              }
            </div>
          </div>
        ) : (
          <textarea
            rows={8}
            value={message.content}
            onChange={(e) => updateMessage({ content: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            placeholder="Enter your message content here..."
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          Use template variables like {'{'}&#123;tenant_name&#125;{'}'}, {'{'}&#123;platform_name&#125;{'}'}, {'{'}&#123;tenant_url&#125;{'}'} for personalization
        </p>
      </div>

      {/* Schedule */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Schedule for Later (Optional)
        </label>
        <input
          type="datetime-local"
          min={new Date().toISOString().slice(0, 16)}
          onChange={(e) => updateMessage({ 
            scheduleFor: e.target.value ? new Date(e.target.value) : undefined 
          })}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        />
      </div>
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        Select a template to pre-populate your message:
      </div>
      {messageTemplates.map((template) => {
        const templateType = messageTypes.find(t => t.id === template.type);
        return (
          <div
            key={template.id}
            onClick={() => handleTemplateSelect(template.id)}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedTemplate === template.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h5 className="font-medium text-gray-900">{template.name}</h5>
                <div className="flex items-center space-x-2 mt-1">
                  {templateType && <templateType.icon className="h-4 w-4 text-gray-500" />}
                  <span className="text-sm text-gray-600 capitalize">{template.type.replace('_', ' ')}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(template.priority)}`}>
                    {template.priority}
                  </span>
                </div>
              </div>
              {selectedTemplate === template.id && (
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <p className="text-sm text-gray-700 font-medium mb-1">{template.subject}</p>
            <p className="text-sm text-gray-600 line-clamp-3">{template.content.slice(0, 200)}...</p>
          </div>
        );
      })}
    </div>
  );

  const renderHistoryTab = () => (
    <div className="text-center py-8">
      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <h4 className="text-lg font-medium text-gray-900 mb-2">Message History</h4>
      <p className="text-gray-600">
        Recent communication history will appear here.
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <PaperAirplaneIcon className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Tenant Communication</h3>
                  <p className="text-sm text-gray-500">
                    Send messages to {selectedTenants.length} selected tenant{selectedTenants.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="px-6 flex space-x-8">
              {[
                { id: 'compose', label: 'Compose' },
                { id: 'templates', label: 'Templates' },
                { id: 'history', label: 'History' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            {activeTab === 'compose' && renderComposeTab()}
            {activeTab === 'templates' && renderTemplatesTab()}
            {activeTab === 'history' && renderHistoryTab()}
          </div>

          {/* Footer */}
          {activeTab === 'compose' && (
            <div className="bg-gray-50 px-6 py-3">
              {showConfirmation ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {selectedType && <selectedType.icon className="h-5 w-5 text-gray-500" />}
                    <span className="text-sm text-gray-700">
                      Send {message.type.replace('_', ' ')} to {selectedTenants.length} tenant{selectedTenants.length !== 1 ? 's' : ''}?
                    </span>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowConfirmation(false)}
                      disabled={isSending}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={isSending}
                      className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <PaperAirplaneIcon className="h-4 w-4 mr-2 inline-block" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowConfirmation(true)}
                    disabled={!validateMessage() || selectedTenants.length === 0}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Review & Send
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantCommunication;